import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchPatient } from '@avanta/shared/hooks/useFetchPatient';
import { useFetchSpecialities } from '@avanta/shared/hooks/useFetchSpecialities';
import { type Patient } from '@avanta/shared/types/patients';
import { ChevronLeft, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, matchPath, Navigate, useLocation, useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { AppointmentCard } from './components/AppointmentCard';
import { AppointmentDetailSkeleton } from './components/AppointmentDetailSkeleton';
import { useFetchAppointmentByPatient } from './useFetchAppointmentByPatient';

export const AppointmentDetail = (): React.ReactElement => {
  const { pathname, state } = useLocation();
  const patientId = state?.appointment?.patientId ?? '';
  const { isLoading: isLoadingAppointments } = useFetchAppointmentByPatient();
  const { data: patient, isLoading: isLoadingPatient } = useFetchPatient(patientId);
  const { isLoading: isLoadingSpecialities } = useFetchSpecialities(patientId);
  const { data: appointment } = useFetchAppointmentByPatient();

  const { isLoading: isLoadingActivePatientRecord } = useFetchActivePatientRecord(patientId);
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  if (!patientId) return <Navigate to={ROUTES.PATIENTS} />;
  const isLoading =
    isLoadingAppointments ||
    isLoadingPatient ||
    isLoadingActivePatientRecord ||
    isLoadingSpecialities;

  const handleOnViewProfile = (): void => {
    navigate(generatePath(ROUTES.PATIENT_PROFILE, { id: patientId }), {
      state: { from: pathname, fromState: state },
    });
  };

  const handleOnBack = (): void => {
    const from = state?.from;
    const fromPatients = matchPath({ path: ROUTES.PATIENT_APPOINTMENT, end: true }, pathname);
    const fromHome = matchPath({ path: ROUTES.HOME_APPOINTMENT_DETAIL, end: true }, pathname);

    if (from === ROUTES.PATIENT_PROFILE) {
      navigate(generatePath(from, { id: patientId }));
      return;
    }

    if (fromPatients) {
      navigate(ROUTES.PATIENTS);
      return;
    }

    if (fromHome) {
      navigate(ROUTES.HOME);
      return;
    }

    navigate(ROUTES.APPOINTMENTS);
  };

  return (
    <div className="flex flex-col gap-8" aria-busy={isLoading}>
      <Button
        variant="link"
        icon="leading"
        className="w-fit -ml-1 -mt-3 mb-1"
        onClick={handleOnBack}
      >
        <ChevronLeft className="size-5" />
        <Text variant="text-sm-medium" className="text-teal-600 hover:text-teal-500">
          {t('AppointmentDetail.back')}
        </Text>
      </Button>
      <Text variant="heading-xl-medium">
        {appointment?.canceled
          ? t('AppointmentsHistory.actions.details')
          : t('InsuredPersonsList.manageAppointment.cta')}
      </Text>
      {isLoading ? (
        <AppointmentDetailSkeleton />
      ) : (
        <>
          <AppointmentCard patient={patient as Patient} />
          <Card className="am-md:px-4 am-md:py-2 am-3xl:rounded-2xl">
            <div className="flex justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-1">
                  <Text variant="text-sm-semibold">{t('AppointmentDetail.patient.label')}</Text>
                  <Text variant="text-sm-regular">
                    {patient?.firstName} {patient?.surname}
                  </Text>
                </div>
                <div className="flex items-center gap-1">
                  <Text variant="text-sm-semibold">{t('InsuredPersonsList.Table.nif.label')}</Text>
                  <Text variant="text-sm-regular">{patient?.nif}</Text>
                </div>
                <div className="flex items-center gap-1">
                  <Text variant="text-sm-semibold">{t('AppointmentDetail.email.label')}</Text>
                  <Text variant="text-sm-regular">{patient?.email}</Text>
                </div>
              </div>
              <Button variant="terciary" size="md" icon="leading" onClick={handleOnViewProfile}>
                <User className="size-5" />
                {t('InsuredPersonsList.viewPerson.cta')}
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};
